// src/components/MapView/Sidebar.jsx
import React from "react";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { selectSelectedGeographyId } from "../../store/mapSlice";
import GeographyDetails from "./GeographyDetails";
import TopGeographies from "./TopGeographies";
import SearchBar from "./SearchBar";
import { useFeatures } from "../../hooks";

/**
 * Sidebar component
 *
 * Desktop (lg+): right-side panel in the flex layout, collapsible via toggle.
 * Mobile/tablet (<lg): bottom sheet overlay with backdrop, controlled by MapView.
 */
const Sidebar = ({
  mobileOpen = false,
  onMobileClose,
  desktopCollapsed = false,
  onDesktopToggle,
}) => {
  const selectedGeographyId = useSelector(selectSelectedGeographyId);
  const { searchEnabled } = useFeatures();

  return (
    <>
      {/* ============================================ */}
      {/* MOBILE/TABLET: backdrop overlay (below lg)   */}
      {/* ============================================ */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/25 z-[1001] transition-opacity"
          onClick={onMobileClose}
          aria-label="Close sidebar"
        />
      )}

      {/* ============================================ */}
      {/* DESKTOP: collapse/expand toggle (lg+)        */}
      {/* ============================================ */}
      <button
        onClick={onDesktopToggle}
        className={`hidden lg:flex absolute top-1/2 transform -translate-y-1/2 z-[1000] bg-white shadow-md p-2 hover:bg-gray-50 transition items-center justify-center ${
          desktopCollapsed ? "right-0 rounded-l-md" : "right-96 rounded-l-md"
        }`}
        aria-label={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {desktopCollapsed ? (
          <ChevronLeft size={20} />
        ) : (
          <ChevronRight size={20} />
        )}
      </button>

      {/* ============================================ */}
      {/* SIDEBAR PANEL                                */}
      {/* <lg: fixed bottom sheet                      */}
      {/* lg+: flex child, collapsible                 */}
      {/* ============================================ */}
      <div
        className={[
          // Shared
          "bg-white flex flex-col overflow-hidden transition-all duration-300",

          // Mobile/tablet: fixed bottom sheet
          "fixed bottom-0 left-0 right-0 z-[1002] rounded-t-2xl shadow-2xl max-h-[75vh]",
          mobileOpen ? "translate-y-0" : "translate-y-full",

          // Desktop (lg+): static sidebar in flex layout
          "lg:static lg:bottom-auto lg:left-auto lg:right-auto",
          "lg:z-auto lg:rounded-none lg:shadow-lg lg:max-h-none lg:translate-y-0",
          desktopCollapsed ? "lg:w-0" : "lg:w-96",
        ].join(" ")}
      >
        {/* Mobile handle bar + close button */}
        <div className="lg:hidden flex-shrink-0">
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <div className="flex-1 flex justify-center">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <button
              onClick={onMobileClose}
              className="p-1.5 -mr-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Sidebar content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {searchEnabled && (
            <div className="p-4 border-b bg-gray-50">
              <SearchBar />
            </div>
          )}

          <div className="p-4 space-y-4">
            {selectedGeographyId ? (
              <GeographyDetails geographyId={selectedGeographyId} />
            ) : (
              <div className="bg-gray-50 rounded-lg p-6 text-center border-2 border-dashed border-gray-200">
                <p className="text-gray-500 text-sm">
                  Click on a geography on the map to see details
                </p>
              </div>
            )}

            <TopGeographies />
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
